<template>
  <div class="workato-integration">
    <div v-if="isLoading">
      <le-loader class="le-loader-center" :size="40"></le-loader>
    </div>
    <IhuModal id="premade-recipes"
              :visible="isPremadeModalVisible"
              @close="hidePremadeRecipeModal">
      <div class="recipe-modal-content">
        <div class="modal-title">
          Explore our pre-built recipes for faster automation
        </div>
        <div class="modal-body">
          <div class="premade-recipe-view-all-action">
            <a class="cursor-pointer" @click="navigateToViewAll">View All</a>
          </div>
          <div v-if="rowCount > 0" class="container-fluid recipes-container">
            <div v-for="rowIndex in rowCount" :key="rowIndex" :class="['recipe-row', { 'last-row': rowIndex === rowCount }]">
              <div v-for="gridIndex in 3" :key="gridIndex" :class="['recipe-grid', { 'last-col': gridIndex === 3 }]">
                <div v-if="workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1]" class="recipe-content-wrapper">
                  <div class="recipe-title">
                    <i :class="recipeIcon(workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].trigger_application)"></i>
                    <span class="recipe-title-text">
                      {{ workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name }}
                    </span>
                  </div>
                  <div class="row recipe-action">
                    <div v-if="workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name === recipeTitle.LEAD_GENERATION ||
                           workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name === recipeTitle.SHOPIFY_UPDATE ||
                           workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name === recipeTitle.BIGCOMMERCE_UPDATE ||
                           workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name === recipeTitle.CART_ABANDONMENT"
                         class="business-card retail mr-10px">
                      Retail
                    </div>
                    <div v-if="workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name === recipeTitle.SHOPIFY_UPDATE"
                         class="business-card restaurant mr-10px">
                      Restaurant
                    </div>
                    <div v-if="workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name === recipeTitle.PAYMENT_REMAINDERS"
                         class="business-card real-estate mr-10px">
                      Real Estate
                    </div>
                    <div v-if="workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].name === recipeTitle.SERVICENOW_UPDATE"
                         class="business-card real-estate mr-10px hidden">
                      Real Estate
                    </div>
                    <IhuButton class="try-recipe-action"
                               variant="secondary"
                               @click="() => navigateToRecipe(workatoRecipe[(rowIndex - 1) * 3 + gridIndex - 1].id)">
                      Try Recipe
                    </IhuButton>
                  </div>
                </div>
                <div v-if="(rowIndex - 1) * 3 + gridIndex === workatoRecipe.length + 1" class="recipe-content-wrapper" @click="() => navigateToRecipe('new')">
                  <div class="start-from-scratch">
                    <span class="circle plus"></span>
                    Start from scratch
                  </div>
                </div>
              </div>
            </div>
            <div class="row-item">
              <IhuCheckbox
                class="modal-checkbox"
                :state="doNotShowModal"
                @change="doNotShowChange">
                <span class="workato-pre-build-recipe-modal-label">Don’t show this message again</span>
              </IhuCheckbox>
            </div>
          </div>
        </div>
      </div>
    </IhuModal>
    <ul class="workato-navbar">
      <li
        v-for="item in menuItems"
        :key="item.key"
        :class="{ 'active-class': item.key === activeMenuItem }"
        @click="() => openWorkatoPage(item)">
        <span v-if="item.key === 'tools'">
          <le-select
            ref="toolsDropdown"
            class="tools-dropdown"
            type="dropdown"
            :items="item.dropdown"
            placeholder="Tools"
            @input="openDropdownSubmenu">
          </le-select>
        </span>
        <span v-else>
          {{ item.name }}
        </span>
      </li>
    </ul>
    <div id="workato-iframe" class="workato-iframe">
      <iframe v-show="activeMenuItem !== 'resources'"
              v-tooltip="{ content: 'Workato iFrame', show: false, trigger: 'manual' }"
              title="Workato iFrame"
              class="IntegrationIframe"
              :src="iframeSrc"></iframe>
      <GetStarted v-show="activeMenuItem === 'resources'"
                  id="getStartedModal"
                  ref="getStartedModal"
                  :workatoActivationPageUrl="workatoActivationPageUrl"></GetStarted>
    </div>
  </div>
</template>

<script>
  import { Vuex } from 'vue-infra';
  import leLoader from '@vueComponents/le-loader/le-loader.vue';
  import leSelect from '@vueComponents/le-select/le-select.vue';
  import { GETTER_TYPES as SHELL_GETTER_TYPES } from '@/store/modules/data/shell/getters';
  import { MODULES_PATHS } from '@/store';
  import { workflowGuardMixin } from '@/mixins';
  import { GETTER_TYPES as BASE_COLLECTION_GETTER_TYPES } from '@/store/modules/data/workato-configuration/getters';
  import { ACTION_TYPES as WORKATO_CONFIGURATION_ACTION_TYPES } from '@/store/modules/data/workato-configuration/actions';
  import { IhuButton, IhuModal, IhuCheckbox } from '@/components/common';
  import { PREMADE_RECIPE_ICON,
           PREMADE_RECIPE_TITLE,
           WORKATO_NAVBAR_ROUTE,
           WORKATO_CONFIG,
           WORKATO_EMBEDDED_DOMAIN,
           WORKATO_DOMAIN_PREFIX_PLACEHOLDER,
           HTTP_STATUS_CODE, FEATURE_FLAGS } from '../../common/consts';
  import GetStarted from './get-started';
  import { DATA_STATES } from '../../store/consts';

  const mapActions = Vuex.mapActions;
  const mapGetters = Vuex.mapGetters;

  export default {
    name: 'workato-integration',
    components: {
      GetStarted,
      IhuModal,
      IhuCheckbox,
      leSelect,
      IhuButton,
      leLoader,
    },

    mixins: [workflowGuardMixin],

    async beforeRouteEnter(to, from, next) {
      next((vm) => {
        // Check if it was direct link or through navigation
        vm.updateIframeSrc('/ihu/integration');
        if (to.params.workatoPath) {
          vm.setDirectLink(to.fullPath);
        }
      });
    },

    async beforeRouteUpdate(to, _, next) {
      if (Workato.loaded) {
        const navigateTo = (this.workatoDirectLink) ? this.workatoDirectLink : to.fullPath;
        this.setDirectLink(null);
        Workato.navigateTo(Workato.extractWorkatoUrl(navigateTo));
        next();
      } else {
        // Removing currently loading iframe
        this.updateIframeSrc(null);
        // Requesting a token and updating an iframe `src`
        this.updateIframeSrc(to.fullPath);
        next();
      }
    },
    props: [],
    data() {
      return {
        workatoDirectLink: null,
        recipeTitle: PREMADE_RECIPE_TITLE,
        extractedUrl: null,
        activeMenuItem: 'assets',
        workatoRecipe: [],
        rowCount: 0,
        isPremadeModalVisible: false,
        menuItems: [{
                      name: 'Assets', key: 'assets', url: WORKATO_NAVBAR_ROUTE.RECIPE,
                    },
                    {
                      name: 'Dashboard', key: 'dashboard', url: WORKATO_NAVBAR_ROUTE.DASHBOARD,
                    },
                    {
                      name: 'Community Library', key: 'community_library', url: WORKATO_NAVBAR_ROUTE.COMMUNITY_LIBRARY,
                    },
                    {
                      name: 'Tools',
                      key: 'tools',
                      dropdown: [{
                        value: 'API platform',
                        id: 'api_platform',
                        url: WORKATO_NAVBAR_ROUTE.API_PLATFORM,
                      }, {
                        value: 'Common data models',
                        id: 'common_data_models',
                        url: WORKATO_NAVBAR_ROUTE.COMMON_DATA_MODELS,
                      }, {
                        value: 'Connector SDK',
                        id: 'connector_sdk',
                        url: WORKATO_NAVBAR_ROUTE.CONNECTOR_SDK,
                      }, {
                        value: 'Lookup tables',
                        id: 'lookup_tables',
                        url: WORKATO_NAVBAR_ROUTE.LOOKUP_TABLES,
                      }, {
                        value: 'Message templates',
                        id: 'message_templates',
                        url: WORKATO_NAVBAR_ROUTE.MESSAGE_TEMPLATES,
                      }, {
                        value: 'On-prem groups',
                        id: 'onprem_groups',
                        url: WORKATO_NAVBAR_ROUTE.ON_PREM_GROUP,
                      }, {
                        value: 'People task',
                        id: 'people_task',
                        url: WORKATO_NAVBAR_ROUTE.PEOPLE_TASK,
                      }, {
                        value: 'Properties',
                        id: 'properties',
                        url: WORKATO_NAVBAR_ROUTE.PROPERTIES,
                      }, {
                        value: 'Pub/Sub',
                        id: 'pub_sub',
                        url: WORKATO_NAVBAR_ROUTE.PUB_SUB,
                      }, {
                        value: 'Recipe lifecycle management',
                        id: 'recipe_lifecycle_management',
                        url: WORKATO_NAVBAR_ROUTE.RECIPE_LIFECYCLE_MANAGEMENT,
                      }, {
                        value: 'Workbot',
                        id: 'workbot',
                        url: WORKATO_NAVBAR_ROUTE.WORKBOT,
                      }],
                    },
                    {
                      name: 'Help & Resources', key: 'resources', url: '',
                    }],
      };
    },
    computed: {
      ...mapGetters(MODULES_PATHS.DATA.WORKATO_SITE_SETTINGS, {
        getWorkatoToken: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_TOKEN,
        getWorkatoTokenRefreshFlag: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_TOKEN_REFRESH_FLAG,
        getWorkatoRecipe: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_RECIPE,
        getDataState: BASE_COLLECTION_GETTER_TYPES.GET_DATA_STATE,
        getWorkatoRecipeLoadState: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_RECIPE_LOAD_STATE,
        getWorkatoConfigValues: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_CONFIG_VALUES,
        getWorkatoRecipeUserFolder: BASE_COLLECTION_GETTER_TYPES.GET_WORKATO_RECIPE_USER_FOLDER,
        doNotShowModal: BASE_COLLECTION_GETTER_TYPES.GET_DO_NOT_SHOW_PRE_BUILD_RECIPES_MODAL,
      }),
      ...mapGetters(MODULES_PATHS.DATA.SHELL, {
        getFeatureFlag: SHELL_GETTER_TYPES.GET_FEATURE_FLAG,
      }),
      isWorkatoPrivateCommunityLibraryEnabled() {
        return this.getFeatureFlag(FEATURE_FLAGS.WORKATO_PRIVATE_COMMUNITY_LIBRARY);
      },
      isLoading() {
        return this.getDataState === DATA_STATES.LOADING || this.getWorkatoRecipeLoadState === DATA_STATES.LOADING;
      },
      workatoEmbeddedDomain() {
        return this.getConfigValue(WORKATO_CONFIG.WORKATO_EMBEDDED_DOMAIN);
      },
      workatoActivationPageUrl() {
        return this.getConfigValue(WORKATO_CONFIG.WORKATO_ACTIVATE_PAGE_VIDEO_URL);
      },
      iframeSrc() {
        if (this.extractedUrl && this.getWorkatoRecipeUserFolder && !this.getWorkatoTokenRefreshFlag) {
          const token = this.getWorkatoToken.token;
          if (this.isWorkatoPrivateCommunityLibraryEnabled) { return Workato.generateIFrameUrl(token, 'browse/recipes'); }

          return Workato.generateIFrameUrl(token, `${this.extractedUrl}recipes?fid=${this.getWorkatoRecipeUserFolder}`);
        }
        return null;
      },
      userFolderSuffix() {
        return this.getWorkatoRecipeUserFolder ? `?fid=${this.getWorkatoRecipeUserFolder}` : '';
      },
      isWorkatoDirectLink() {
        try {
          return !!this.$route.params.workatoPath;
        } catch (e) {
          throw new Error(Object.keys(this));
        }
      },
    },

    destroyed() {
      Workato.disableNavigationHandling();
      this.setWorkatoTokenRefreshFlag(true);
    },
    async created() {
      const recipesRequest = this.fetchWorkatoRecipe();
      const recipeModalFlagRequest = this.fetchDoNotShowPreBuildRecipeModalFlag();
      const ihubConfigRequest = this.fetchIhubConfig({
        [WORKATO_CONFIG.WORKATO_EMBEDDED_DOMAIN]: WORKATO_CONFIG.WORKATO_EMBEDDED_DOMAIN,
        [WORKATO_CONFIG.WORKATO_ACTIVATE_PAGE_VIDEO_URL]: WORKATO_CONFIG.WORKATO_ACTIVATE_PAGE_VIDEO_URL,
      });
      await Promise.allSettled([recipesRequest, recipeModalFlagRequest, ihubConfigRequest]);
      if (!this.isWorkatoDirectLink) {
        await this.showPremadeRecipeModal();
      }
    },
    mounted() {
      this.setupWorkatoRouteHandling();
    },
    methods: {
      ...mapActions(MODULES_PATHS.DATA.WORKATO_SITE_SETTINGS, {
        fetchWorkatoRecipe: WORKATO_CONFIGURATION_ACTION_TYPES.FETCH_WORKATO_RECIPE,
        fetchIhubConfig: WORKATO_CONFIGURATION_ACTION_TYPES.FETCH_IHUB_CONFIG,
        setWorkatoTokenRefreshFlag: WORKATO_CONFIGURATION_ACTION_TYPES.SET_WORKATO_TOKEN_REFRESH_FLAG,
        setDoNotShowPreBuildRecipeModalFlag: WORKATO_CONFIGURATION_ACTION_TYPES.SET_DO_NOT_SHOW_PRE_BUILD_RECIPE_MODAL_FLAG,
        fetchDoNotShowPreBuildRecipeModalFlag: WORKATO_CONFIGURATION_ACTION_TYPES.GET_DO_NOT_SHOW_PRE_BUILD_RECIPE_MODAL_FLAG,
      }),
      getConfigValue(key) {
        return this.getWorkatoConfigValues[key] || null;
      },
      setDirectLink(path) {
        this.workatoDirectLink = path;
      },
      setupWorkatoRouteHandling() {
          Workato.handleNavigation({
          onWorkatoNavigation: (event) => {
            const {
              workatoUrl,
              embeddingUrl,
              urlReplaced,
              preventVendorUrlChange,
            } = event;
            preventVendorUrlChange();
            this.adjustActiveMenuItem(workatoUrl);
            if (urlReplaced) {
              this.$router.replace(embeddingUrl);
            } else {
              this.$router.push(embeddingUrl);
            }
          },
          onVendorNavigation(event) {
            if (event.reason === 'link') {
              event.preventVendorUrlChange();
            }
            event.preventWorkatoUrlChange();
          },
        });
      },
      adjustActiveMenuItem(workatoUrl) {
        try {
            // eslint-disable-next-line compat/compat
            const workatoPathname = new URL(this.workatoEmbeddedDomain + workatoUrl).pathname;
          // eslint-disable-next-line no-restricted-syntax
          for (const item of this.menuItems) {
            const menuItemPathname = item.url.replace(WORKATO_DOMAIN_PREFIX_PLACEHOLDER, '');
            if (workatoPathname === menuItemPathname) {
              this.activeMenuItem = item.key;
            }
          }
        } catch (e) {
          console.info('something went wrong');
        }
      },
      updateIframeSrc(embeddingUrl) {
        if (embeddingUrl && this.getWorkatoToken.token) {
          this.extractedUrl = Workato.extractWorkatoUrl(embeddingUrl);
        }
      },
      getWorkatoPathFromBrowser() {
        return this.$route.params.workatoPath;
      },
      openWorkatoPage(page) {
        this.activeMenuItem = page.key;
        const url = page.key === 'assets' ? `${page.url}${this.userFolderSuffix}` : page.url;
        this.setAndNavigateToWorkatoUrl(url);
      },
      openDropdownSubmenu(dropdown) {
        this.setAndNavigateToWorkatoUrl(dropdown.url);
      },
      setAndNavigateToWorkatoUrl(url) {
        const urlResolver = window.LE.urlResolver;
        urlResolver.addConfigValue(WORKATO_EMBEDDED_DOMAIN, this.workatoEmbeddedDomain);
        const baseURL = urlResolver.getUrl({ url });
        Workato.navigateTo(baseURL);
      },
      async showPremadeRecipeModal() {
        const existingWorkatoRecipe = await this.getWorkatoRecipe;
        if (this.doNotShowModal) {
          return;
        }
        if (existingWorkatoRecipe.responseCode === HTTP_STATUS_CODE.OK && existingWorkatoRecipe.premadeRecipe.length > 0) {
          this.workatoRecipe = existingWorkatoRecipe.premadeRecipe;
        }
        this.rowCount = Math.floor((this.workatoRecipe.length / 3) + 1);
        if (!this.isWorkatoPrivateCommunityLibraryEnabled) { this.isPremadeModalVisible = true; }
      },
      recipeIcon(triggerApplication) {
        let style = 'icon-32 recipe-icon ';
        if (PREMADE_RECIPE_ICON.SHOPIFY === triggerApplication) {
          style += 'ic_shopify-2x';
        } else if (PREMADE_RECIPE_ICON.SYSTRAN === triggerApplication) {
          style += 'ic_systran';
        } else if (PREMADE_RECIPE_ICON.SALESFORCE === triggerApplication) {
          style += 'ic_salesforce-2x';
        } else if (PREMADE_RECIPE_ICON.QUICKBOOKS === triggerApplication) {
          style += 'ic_quickbooks-2x';
        } else if (PREMADE_RECIPE_ICON.SERVICENOW === triggerApplication) {
          style += 'ic_servicenow-2x';
        } else if (PREMADE_RECIPE_ICON.BIGCOMMERCE === triggerApplication) {
          style += 'ic_bigcommerce-2x';
        }
        return style;
      },
      hidePremadeRecipeModal() {
        this.isPremadeModalVisible = false;
      },
      navigateToViewAll() {
        this.hidePremadeRecipeModal();
        const url = this.userFolderSuffix ? (WORKATO_NAVBAR_ROUTE.RECIPE + this.userFolderSuffix) : WORKATO_NAVBAR_ROUTE.VIEW_ALL_RECIPE;
        this.setAndNavigateToWorkatoUrl(url);
      },
      navigateToRecipe(recipe) {
        if (recipe === 'new') {
          this.setAndNavigateToWorkatoUrl(WORKATO_NAVBAR_ROUTE.NEW_RECIPE);
        } else if (recipe) {
          this.setAndNavigateToWorkatoUrl(`${WORKATO_NAVBAR_ROUTE.RECIPE}/${recipe}`);
        }
        this.hidePremadeRecipeModal();
      },
      doNotShowChange() {
        this.setDoNotShowPreBuildRecipeModalFlag(!this.doNotShowModal);
      },
    },
  };


</script>

<style scoped lang="scss">

//workato iframe style
.workato-integration {
  .mr-10px {
    margin-right: 10px !important;
  }
  .hidden {
    visibility: hidden;
  }
  padding: 0 3rem 0 3rem;
  height: 100%;
  .workato-iframe {
    display: flex;
    height: calc(100% - (122px));
    @include themify {
      color: getColor('workato-text', 'ihu') !important;
      border: 1px solid getColor('workato-get-started-content-grid-border', 'ihu') !important;
    }
    .IntegrationIframe {
      border: none;
      width: 100%;
      height: 100%;
    }
  }
  .recipe-modal-content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    height: 100%;

    @include themify {
      color: getColor('text');
      background-color: getColor('premade-recipe-background', 'ihu') !important;
    }
    .modal-title {
      align-self: flex-start;
      padding: 2.125rem 0 0 1.5625rem;
      font-size: 16px;
      @include themify {
        color: getColor('premade-recipe-header-text', 'ihu') !important;
      }
    }
    .modal-body {
      padding: 0rem 1.5625rem;
      position: relative;

      .premade-recipe-view-all-action {
        position: absolute;
        right: 0;
        margin-right: 2.5rem;
        font-size: 16.8px;
        line-height: 120%;
        a {
          text-decoration: none;
          @include themify {
            color: getColor('premade-recipe-highlight-text', 'ihu') !important;
          }
        }
      }

      .recipes-container {
        padding: 2.125rem 0;

        .recipe-row {
          display: flex;
          margin: 1.5rem 0;
          box-sizing: content-box;

          &:first-child {
            margin-top: 0;
          }

          &.last-row {
            margin-bottom: 0;
          }

          .recipe-grid {
            margin: 0 .75rem;
            width: 100%;
            height: 100%;

            &:first-child {
              margin-left: 0;
            }

            &.last-col {
              margin-right: 0;
            }

            .recipe-content-wrapper {
              box-sizing: content-box;
              width: 280px;
              height: 142px;
              border-radius: 12px;
              padding: 5px 25px;
              box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.1);
              position: relative;
              display: flex;
              flex-direction: column;
              justify-content: space-between;

              @include themify {
                background-color: getColor('premade-recipe-content-background', 'ihu') !important;
                color: getColor('premade-recipe-content-title', 'ihu') !important;
              }
              @include themify('light') {
                border: 1px solid rgba(140, 172, 255, 0.3);
              }

              .recipe-title {
                display: flex;
                font-size: 16px;
                height: 5rem;
                line-height: 150%;
                vertical-align: center;
                align-items: center;

                .recipe-icon {
                  padding: 0 .5rem;
                }

                .recipe-title-text {
                  text-align: start;
                }
              }

              .recipe-action {
                display: flex;
                justify-content: space-between;
                margin-bottom: 1.5rem;

                .business-card{
                  padding: 3px 10px;
                  border-radius: 1rem;
                  height: fit-content;
                  width: fit-content;
                  font-size: 13px;
                  margin: auto 0;
                }
                .retail{
                  @include themify {
                    border: 1px solid getColor('premade-recipe-content-retail-business-text', 'ihu');
                    color: getColor('premade-recipe-content-retail-business-text', 'ihu');
                  }
                }
                .restaurant{
                  @include themify {
                    border: 1px solid getColor('premade-recipe-content-restaurant-business-text', 'ihu');
                    color: getColor('premade-recipe-content-restaurant-business-text', 'ihu');
                  }
                }
                .real-estate{
                  @include themify {
                    border: 1px solid getColor('premade-recipe-content-real-estate-business-text', 'ihu');
                    color: getColor('premade-recipe-content-real-estate-business-text', 'ihu');
                  }
                }
                .finance{
                  @include themify {
                    border: 1px solid getColor('premade-recipe-content-finance-business-text', 'ihu');
                    color: getColor('premade-recipe-content-finance-business-text', 'ihu');
                  }
                }
                .try-recipe-action {
                  display: inline-block;
                  align-self: flex-end;
                }
                .try-recipe-action:hover{
                  @include themify {
                    color: getColor('workato-button-primary-color', 'ihu') !important;
                    border-color: getColor('workato-button-primary-background', 'ihu') !important;
                    background-color: getColor('workato-button-primary-background', 'ihu') !important;
                  }
                }
              }
            }

            .start-from-scratch {
              text-align: center;
              padding-top: 9%;
              font-size: 16px;
              @include themify {
                color: getColor('premade-recipe-highlight-text', 'ihu') !important;;
              }
              .circle:before {
                cursor: pointer;
                content: '';
                display: flex;
                justify-content: center;
                align-items: center;
                width: 3rem;
                height: 3rem;
                border-radius: 50%;
                border: 2px solid;
                font-size: 30px;
                margin: 0.5rem auto 1rem;
              }
              .plus:before {
                content: '\FF0B';
              }
            }
          }
        }
      }

      .row-item {
        padding: 2rem .1rem .5rem;
        text-align: left;

        .modal-checkbox {
          @include themify {
            color: getColor('card-main-color', 'ihu') !important;
          }

          .workato-pre-build-recipe-modal-label {
            margin-left: 8px;
            font-size: 14px;
            line-height: 150%;
          }
        }
      }
    }
  }

  .workato-navbar {
    font-family: Roboto;
    @include themify {
      background-color: getColor('workflow-navbar-background', 'ihu') !important;
    }
    border-radius: 6px 6px 0 0;
    padding: 1rem 0 1rem 3.5rem;
    line-height: 1rem;
    height: 1rem;
    text-align: left;
    display: flex;
    margin-bottom: 0px;
    li {
      display: flex;
      margin: 0 1.5rem;
      text-decoration: none;
      font-size: 14px;
      cursor: pointer;
      @include themify {
        color: getColor('workflow-navbar-color', 'ihu') !important;
      }
      i {
        @include themify('dark') {
          background-image: url('./../../../assets/img/ic_getstarted3x.png');
        }
        @include themify('light') {
          background-image: url('./../../../assets/img/ic_light_getstarted3x.png');
        }
      }
      .tools-dropdown {
        top: -7px;
        width: 100%;
        /deep/ .le-select-dropdown {
          border: none !important;
          background-color: transparent !important;
          .placeholder {
            font-size: 14px;
            max-width: 100% !important;
            padding-left: 0px;
            color: inherit;
          }
        }
        /deep/ .le-select-list {
          max-height: none !important;
          font-size: 14px;
          font-weight: normal;
          min-width: max-content !important;
          border-radius: 6px;
          border: 0px;
          top: 43px;
          left: -100px;
          @include themify {
            background-color: getColor('workflow-tools-dropdown-background', 'ihu') !important;
            color: getColor('workflow-tools-dropdown-color', 'ihu') !important;
          }
          .list-item-container{
            min-width: fit-content;
            padding: 1.5rem 1.5rem;
            height: 1rem;
            margin: 0;
          }
          .list-item-container:hover {
            @include themify {
              background-color: getColor('workflow-tools-dropdown-hover-background', 'ihu') !important;
            }
            .le-text-with-highlight{
              white-space: normal;
              @include themify {
                color: getColor('workflow-tools-dropdown-hover-color', 'ihu') !important;
              }
            }
          }
          .pointed {
            background-color: inherit;
          }
        }
      }
    }
    .active-class {
      font-weight: bold;
      margin-bottom: -17px;
      border-bottom: 3px solid #E76008;
      border-radius: 4px 4px 0 0;
      @include themify {
        color: getColor('workflow-navbar-color-active', 'ihu') !important;
      }
    }
  }
}
</style>
